import { useState } from 'react'
import { FreetextSuggestion, ResolveFreetextInput } from '@/types/admin'
import { useFreetextSuggestions, useResolveFreetext, useMergeFreetext } from '@/hooks/useFreetext'
import { FreetextTable } from '@/components/freetext/FreetextTable'
import { ResolveFreetextDialog } from '@/components/freetext/ResolveFreetextDialog'
import { MergeFreetextDialog } from '@/components/freetext/MergeFreetextDialog'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Separator } from '@brent/core-ui/ui'
import { ManagementTabs } from '@/components/shared/ManagementTabs'
import { useToast } from '@/hooks/use-toast'
import { AlertCircle, GitMerge } from 'lucide-react'

export default function Freetext() {
  const [selectedSuggestion, setSelectedSuggestion] = useState<FreetextSuggestion | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const { toast } = useToast()

  // Fetch unresolved and resolved freetext suggestions
  const {
    data: unresolvedSuggestions = [],
    isLoading: isLoadingUnresolved,
    error: unresolvedError,
  } = useFreetextSuggestions(false)

  const {
    data: resolvedSuggestions = [],
    isLoading: isLoadingResolved,
    error: resolvedError,
  } = useFreetextSuggestions(true)

  const resolveMutation = useResolveFreetext()
  const mergeMutation = useMergeFreetext()

  const handleResolveClick = (suggestion: FreetextSuggestion) => {
    setSelectedSuggestion(suggestion)
    setDialogOpen(true)
  }

  const handleResolve = (suggestionId: string, input: ResolveFreetextInput) => {
    resolveMutation.mutate(
      { suggestionId, input },
      {
        onSuccess: () => {
          toast({
            title: 'Success',
            description: 'Freetext entry resolved successfully',
          })
          setDialogOpen(false)
          setSelectedSuggestion(null)
        },
        onError: (error: Error) => {
          toast({
            title: 'Error',
            description: error?.message || 'Failed to resolve freetext entry',
            variant: 'destructive',
          })
        },
      },
    )
  }

  const handleMergeClick = () => {
    if (selectedIds.size !== 2) {
      toast({
        title: 'Error',
        description: 'Please select exactly 2 entries to merge',
        variant: 'destructive',
      })
      return
    }
    setMergeDialogOpen(true)
  }

  const handleMerge = (
    masterId: string,
    duplicateId: string,
    mergeComposer: boolean,
    mergeWork: boolean,
  ) => {
    mergeMutation.mutate(
      { masterId, duplicateId, mergeComposer, mergeWork },
      {
        onSuccess: () => {
          toast({
            title: 'Success',
            description: 'Freetext entries de-duplicated successfully',
          })
          setMergeDialogOpen(false)
          setSelectedIds(new Set())
          setSelectionMode(false)
        },
        onError: (error: Error) => {
          toast({
            title: 'Error',
            description: error?.message || 'Failed to de-duplicate freetext entries',
            variant: 'destructive',
          })
        },
      },
    )
  }

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode)
    setSelectedIds(new Set())
  }

  const getSelectedSuggestions = (): [FreetextSuggestion, FreetextSuggestion] | null => {
    if (selectedIds.size !== 2) return null
    const ids = Array.from(selectedIds)
    // Allow selection from both unresolved and resolved tables
    const allSuggestions = [...unresolvedSuggestions, ...resolvedSuggestions]
    const suggestions = allSuggestions.filter((s) => ids.includes(s.suggestion_id))
    if (suggestions.length !== 2) return null
    return [suggestions[0], suggestions[1]]
  }

  const isLoading = isLoadingUnresolved || isLoadingResolved
  const error = unresolvedError || resolvedError

  return (
    <div className="space-y-6">
      <ManagementTabs />

      <div className="flex items-start justify-between">
        <p className="text-muted-foreground">
          Manage custom entries that members added when Wikidata lookup failed
        </p>
        <div className="flex gap-2">
          {selectionMode ? (
            <>
              <Button variant="outline" onClick={toggleSelectionMode}>
                Cancel
              </Button>
              <Button onClick={handleMergeClick} disabled={selectedIds.size !== 2}>
                <GitMerge className="mr-2 h-4 w-4" />
                Merge Selected ({selectedIds.size}/2)
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={toggleSelectionMode}>
              <GitMerge className="mr-2 h-4 w-4" />
              Merge Duplicates
            </Button>
          )}
        </div>
      </div>

      {selectionMode && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Select 2 entries to merge (can select from both unresolved and resolved tables). The
            target entry will be updated with the source's Wikidata IDs. Both suggestions will be
            preserved.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load freetext suggestions: {(error as Error).message}
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading freetext entries...</div>
        </div>
      ) : (
        <>
          <FreetextTable
            suggestions={unresolvedSuggestions}
            onResolve={handleResolveClick}
            showResolved={false}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            selectionMode={selectionMode}
          />

          <Separator className="my-8" />

          <FreetextTable
            suggestions={resolvedSuggestions}
            onResolve={handleResolveClick}
            showResolved={true}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            selectionMode={selectionMode}
          />
        </>
      )}

      <ResolveFreetextDialog
        suggestion={selectedSuggestion}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onResolve={handleResolve}
        isResolving={resolveMutation.isPending}
      />

      <MergeFreetextDialog
        suggestions={getSelectedSuggestions()}
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        onMerge={handleMerge}
        isMerging={mergeMutation.isPending}
      />
    </div>
  )
}
